import {
  chartChipFill,
  chartChipStroke,
  chartGrid,
  chartInk,
  chartMarkerLine,
  chartMuted,
  chartRailGround,
} from "./theme.js";
import {
  colors,
} from "../core/config.js";
import {
  clamp01,
} from "../core/math.js";
import {  chartMarkerX,
  detectionHighlightStyle,
  detectionWindowGeometry,
  detectionWindowLabelPlan,
  emptyChartMessagePlan,
  inlineLegendItems,
  laneLabelPlan,
} from "./presentation.js";
import {
  valueRange,
} from "./scales.js";
import { lerp } from "../core/math.js";

function scalePoint(value, min, max, low, high) {
  return high - ((value - min) / (max - min)) * (high - low);
}

export function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function normalizedLabelTicks(labels) {
  return labels.map((item, index) => {
    if (item && typeof item === "object") return item;
    return {
      label: item,
      ratio: labels.length > 1 ? index / (labels.length - 1) : 0,
    };
  });
}

export function drawGrid(ctx, canvas, bounds, yLabels = [], options: Record<string, any> = {}) {
  if (options.clear !== false) ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.strokeStyle = chartGrid();
  ctx.lineWidth = 1;
  ctx.font = options.labelFont ?? "14px Inter, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const leftTicks = normalizedLabelTicks(yLabels);
  const rightTicks = normalizedLabelTicks(options.rightLabels ?? []);

  leftTicks.forEach((tick) => {
    const y = lerp(bounds.top, bounds.bottom, clamp01(tick.ratio));
    ctx.beginPath();
    ctx.moveTo(bounds.left, y);
    ctx.lineTo(bounds.right, y);
    ctx.stroke();
    if (tick.label) {
      ctx.fillStyle = options.leftLabelColor ?? chartMuted();
      ctx.textAlign = "left";
      ctx.fillText(tick.label, options.leftLabelX ?? Math.max(8, bounds.left - 38), y + 4);
    }
  });

  rightTicks.forEach((tick) => {
    if (tick.label) {
      const y = lerp(bounds.top, bounds.bottom, clamp01(tick.ratio));
      ctx.fillStyle = options.rightLabelColor ?? chartMuted();
      ctx.textAlign = "left";
      ctx.fillText(tick.label, bounds.right + (options.rightLabelOffset ?? 10), y + 4);
    }
  });

  for (let index = 0; index <= 5; index += 1) {
    const x = lerp(bounds.left, bounds.right, index / 5);
    ctx.beginPath();
    ctx.moveTo(x, bounds.top);
    ctx.lineTo(x, bounds.bottom);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawEmptyChartMessage(ctx, canvas, bounds, message) {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  drawGrid(ctx, canvas, bounds, []);
  ctx.save();
  ctx.fillStyle = chartMuted();
  ctx.font = "700 18px Inter, sans-serif";
  const plan = emptyChartMessagePlan(ctx, bounds, message);
  ctx.fillText(plan.message, plan.x, plan.y);
  ctx.restore();
}

export function drawMarker(ctx, bounds, ratio) {
  const x = chartMarkerX(bounds, ratio);
  ctx.save();
  ctx.strokeStyle = chartMarkerLine();
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(x, bounds.top);
  ctx.lineTo(x, bounds.bottom);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

export function drawLaneLabel(ctx, bounds, label, options: Record<string, any> = {}) {
  ctx.save();
  ctx.font = "700 13px Inter, sans-serif";
  const plan = laneLabelPlan(ctx, bounds, label, options);
  const { rect } = plan;
  ctx.fillStyle = chartChipFill();
  roundRect(ctx, rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top, 6);
  ctx.fill();
  ctx.strokeStyle = chartChipStroke();
  ctx.stroke();
  ctx.fillStyle = plan.color;
  ctx.fillText(plan.label, plan.x, plan.y + 2);
  ctx.restore();
  return rect;
}

export function drawInlineLegend(ctx, bounds, labels, legendColors) {
  ctx.font = "13px Inter, sans-serif";
  inlineLegendItems(bounds, labels, legendColors).forEach((item) => {
    ctx.strokeStyle = item.color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(item.line.x1, item.line.y1);
    ctx.lineTo(item.line.x2, item.line.y2);
    ctx.stroke();
    ctx.fillStyle = chartInk();
    ctx.fillText(item.label, item.text.x, item.text.y);
  });
}

export function detectionWindowGeometries(data, bounds, windows = []) {
  return windows
    .map((window) => detectionWindowGeometry(data, bounds, window))
    .filter(Boolean);
}

function rectsOverlap(a, b) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function mergedDetectionLabelGeometries(ctx, geometries, bounds, reservedRects = []) {
  const plans = geometries
    .map((geometry) => ({ geometry, plan: detectionWindowLabelPlan(ctx, geometry, bounds, reservedRects) }))
    .sort((a, b) => a.plan.rect.left - b.plan.rect.left);
  const groups = [];
  plans.forEach((item) => {
    const last = groups.at(-1);
    if (!last || !rectsOverlap(last.rect, item.plan.rect)) {
      groups.push({
        items: [item],
        rect: { ...item.plan.rect },
      });
      return;
    }
    last.items.push(item);
    last.rect = {
      left: Math.min(last.rect.left, item.plan.rect.left),
      right: Math.max(last.rect.right, item.plan.rect.right),
      top: Math.min(last.rect.top, item.plan.rect.top),
      bottom: Math.max(last.rect.bottom, item.plan.rect.bottom),
    };
  });

  return groups.map((group) => {
    if (group.items.length === 1) return group.items[0].geometry;
    const items = group.items.map((item) => item.geometry);
    const first = items.find((item) => item.window?.isPrimaryOperatingContext)?.window ?? items[0].window;
    return {
      window: {
        ...first,
        label: first.label,
      },
      x: Math.min(...items.map((item) => item.x)),
      right: Math.max(...items.map((item) => item.right)),
      width: Math.max(3, Math.max(...items.map((item) => item.right)) - Math.min(...items.map((item) => item.x))),
    };
  });
}

export function drawSelectedDetectionWindow(ctx, data, bounds, windows = []) {
  const geometries = detectionWindowGeometries(data, bounds, windows);
  if (!geometries.length) return;

  ctx.save();
  geometries.forEach(({ window, x, width }) => {
    const style = detectionHighlightStyle(window.key);
    ctx.fillStyle = style.fill;
    ctx.fillRect(x, bounds.top, width, bounds.bottom - bounds.top);
    ctx.strokeStyle = style.stroke;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 5]);
    ctx.strokeRect(x, bounds.top, width, bounds.bottom - bounds.top);
    ctx.setLineDash([]);
  });
  ctx.restore();
}

export function drawDetectionWindowLabel(ctx, data, bounds, windows = [], reservedRects = []) {
  const geometries = detectionWindowGeometries(data, bounds, windows);
  const labelGeometries = mergedDetectionLabelGeometries(ctx, geometries, bounds, reservedRects);
  let lastRect = null;
  labelGeometries.forEach((geometry) => {
    const { displayLabel, labelX, labelY, rect, style } = detectionWindowLabelPlan(ctx, geometry, bounds, reservedRects);

    ctx.save();
    ctx.font = "700 12px Inter, sans-serif";
    ctx.fillStyle = chartRailGround();
    roundRect(ctx, rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top, 6);
    ctx.fill();
    ctx.strokeStyle = style.stroke;
    ctx.stroke();
    ctx.fillStyle = style.text;
    ctx.fillText(displayLabel, labelX, labelY + 1);
    ctx.restore();
    reservedRects.push(rect);
    lastRect = rect;
  });
  return lastRect;
}


export function drawLine(ctx, data, key, meta, bounds, options: Record<string, any> = {}) {
  const hasProvidedRange = Number.isFinite(options.min) && Number.isFinite(options.max);
  let min = options.min;
  let max = options.max;
  if (!hasProvidedRange) {
    const validData = data.filter((point) => Number.isFinite(point[key]));
    if (validData.length < 2) return;
    const range = valueRange(validData, key, meta, Boolean(options.independentScale));
    min = range.min;
    max = range.max;
  }
  const dash = options.dash ?? [];
  ctx.save();
  ctx.beginPath();
  ctx.rect(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top);
  ctx.clip();
  ctx.strokeStyle = options.color ?? meta.color;
  ctx.lineWidth = options.width ?? 2.3;
  ctx.setLineDash(dash);
  ctx.beginPath();
  if (!dash.length && data.length > Math.max(900, (bounds.right - bounds.left) * 1.2)) {
    drawDecimatedLinePath(ctx, data, key, bounds, min, max);
    ctx.stroke();
    ctx.restore();
    return;
  }
  let hasActiveSegment = false;
  data.forEach((point, index) => {
    const x = lerp(bounds.left, bounds.right, index / Math.max(data.length - 1, 1));
    if (!Number.isFinite(point[key])) {
      hasActiveSegment = false;
      return;
    }
    const y = scalePoint(point[key], min, max, bounds.top, bounds.bottom);
    hasActiveSegment ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    hasActiveSegment = true;
  });
  ctx.stroke();
  ctx.restore();
}

function drawDecimatedLinePath(ctx, data, key, bounds, min, max) {
  const width = Math.max(1, bounds.right - bounds.left);
  const bucketCount = Math.ceil(width);
  const bucketSize = Math.max(1, Math.ceil(data.length / bucketCount));
  let hasActiveSegment = false;

  for (let start = 0; start < data.length; start += bucketSize) {
    const end = Math.min(data.length, start + bucketSize);
    let minCandidate = null;
    let maxCandidate = null;

    for (let index = start; index < end; index += 1) {
      const value = data[index][key];
      if (!Number.isFinite(value)) continue;
      const candidate = { index, value };
      if (!minCandidate || value < minCandidate.value) minCandidate = candidate;
      if (!maxCandidate || value > maxCandidate.value) maxCandidate = candidate;
    }

    if (!minCandidate && !maxCandidate) {
      hasActiveSegment = false;
      continue;
    }

    const candidates = minCandidate?.index === maxCandidate?.index
      ? [minCandidate]
      : [minCandidate, maxCandidate];
    candidates.sort((a, b) => a.index - b.index);

    candidates.forEach((candidate) => {
      const x = lerp(bounds.left, bounds.right, candidate.index / Math.max(data.length - 1, 1));
      const y = scalePoint(candidate.value, min, max, bounds.top, bounds.bottom);
      hasActiveSegment ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      hasActiveSegment = true;
    });
  }
}
